import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ArrowLeftRight,
  TableProperties,
  BookOpen,
  BarChart2,
  Download,
  FileText,
} from "lucide-react";

type NavItem = { icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>; label: string; path: string; cabinetPath?: string };

export const gestionItems: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", cabinetPath: "/cabinet" },
  { icon: ArrowLeftRight, label: "Transactions", path: "/transactions" },
  { icon: TableProperties, label: "Saisie opérations", path: "/gestion/transactions/saisie" },
  { icon: BookOpen, label: "Comptabilité", path: "/comptabilite" },
  { icon: BarChart2, label: "Bilan & Résultat", path: "/bilan" },
  { icon: Download, label: "Factures d'achat", path: "/gestion/factures-achat" },
  { icon: FileText, label: "Factures de vente", path: "/gestion/factures-vente" },
];

/** Chemins partagés : accessibles depuis Gestion OU Compta selon le contexte de navigation. */
function isSharedPath(path: string): boolean {
  return (
    path.startsWith("/transactions") ||
    path.startsWith("/gestion/factures-vente") ||
    path.startsWith("/gestion/produits/import") ||
    path === "/export/operations" ||
    path === "/bilan"
  );
}

const iconSize = 20;
const iconStroke = 1.8;

export interface GestionMenuProps {
  isCabinetFirm: boolean;
  sidebarCollapsed: boolean;
  onFacturationSidebarOpen: (open: boolean) => void;
  onFacturesAchatSidebarOpen: (open: boolean) => void;
  typeComptabilite?: "tresorerie" | "engagement" | null;
}

export default function GestionMenu({
  isCabinetFirm,
  sidebarCollapsed,
  onFacturationSidebarOpen,
  onFacturesAchatSidebarOpen,
  typeComptabilite,
}: GestionMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isTresorerie = typeComptabilite === "tresorerie";
  const visibleItems = isTresorerie
    ? gestionItems.filter(
        (i) =>
          i.path === "/dashboard" || i.path === "/transactions" || i.path === "/gestion/transactions/saisie"
      )
    : gestionItems;
  const items = visibleItems.map((i) => {
    let path = i.path;
    if (i.path === "/dashboard") path = isCabinetFirm ? "/cabinet" : "/dashboard";
    return { ...i, path };
  });

  return (
    <>
      {items.map((item) => {
        const isActive =
          location.pathname === item.path ||
          (item.path === "/transactions" &&
            (location.pathname === "/transactions" || location.pathname.startsWith("/transactions/"))) ||
          (item.path === "/gestion/transactions/saisie" &&
            location.pathname.startsWith("/gestion/transactions")) ||
          (item.path === "/gestion/factures-vente" && location.pathname.startsWith("/gestion/factures-vente")) ||
          (item.path === "/gestion/factures-achat" && location.pathname.startsWith("/gestion/factures-achat"));
        const isFacturesVente = item.path === "/gestion/factures-vente";
        const isFacturesAchat = item.path === "/gestion/factures-achat";
        const handleClick = isFacturesVente
          ? () => {
              navigate("/gestion/factures-vente", { state: { navFrom: "gestion" } });
              onFacturationSidebarOpen(true);
              onFacturesAchatSidebarOpen(false);
            }
          : isFacturesAchat
          ? () => {
              onFacturesAchatSidebarOpen(true);
              onFacturationSidebarOpen(false);
            }
          : () => {
              const targetPath = item.path;
              const state = isSharedPath(targetPath) ? { state: { navFrom: "gestion" as const } } : {};
              navigate(targetPath, state);
              onFacturationSidebarOpen(false);
              onFacturesAchatSidebarOpen(false);
            };
        return (
          <div
            key={item.path}
            className={`nav-item ${isActive ? "active" : ""}`}
            onClick={handleClick}
            title={sidebarCollapsed ? item.label : ""}
            style={{
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              paddingRight: "16px",
            }}
          >
            <span className="nav-icon"><item.icon size={iconSize} strokeWidth={iconStroke} /></span>
            {!sidebarCollapsed && <span className="nav-label" style={{ flex: 1 }}>{item.label}</span>}
          </div>
        );
      })}
    </>
  );
}
